import { useFieldArray, useWatch } from 'react-hook-form';
import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';

import { useSolcelleFormContext } from '../../utils';
import { PanelTypeRadio } from '../panelTypeRadio';
import { InfoPopover } from '../infoPopover';
import { PanelCard, PanelRowWrapper, PanelWrapper, RemoveButtonWrapper } from '../styledComponents';
import { SolarOrientationInput } from '../solarOrientationGuide';
import { SolarPanelAmountInput } from '../solarPanelAmountInput';
import { useSolcellerValidation } from '../../useSolcellerValidation';
import { RemoveButton } from '../removeButton';
import { hasNorthFacingAzimuth } from '../solarPanelWarnings';

import { SolcelletaksteinInput } from './solcelletaksteinInput';

export const SqmPanels = () => {
  const { t } = useTranslation();
  const { validateAzimuth, validateTilt, validateArea, integerPattern, decimalPattern } = useSolcellerValidation();
  const { control, formState } = useSolcelleFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'sqmPanels', control });
  const { errors } = formState;

  const sqmPanels = useWatch({ control, name: 'sqmPanels' });
  const hasSolcelletakstein = (index: number) => sqmPanels?.[index]?.panelType === 'solcelletakstein';
  const hasSolcellepanel = (index: number) => sqmPanels?.[index]?.panelType === 'solcellepanel';
  const showNorthFacingAzimuthWarning = hasNorthFacingAzimuth(sqmPanels?.filter((panel) => panel.panelType === 'solcellepanel'));

  return (
    <PanelWrapper>
      <div>
        <Heading data-size="xs" level={3}>
          {t('register.solar.form.typeAndPlacement.label')}
        </Heading>
        <Paragraph>{t('register.solar.form.typeAndPlacement.info')}</Paragraph>
      </div>
      <InfoPopover />
      {fields.map((field, index) => (
        <PanelCard key={field.id}>
          <PanelTypeRadio fieldPath={`sqmPanels.${index}.panelType`} error={errors.sqmPanels?.[index]?.panelType} />
          <PanelRowWrapper aria-label={t('register.solar.form.orientationGuide.ariaLabel')} role="group">
            <SolarOrientationInput
              kind="azimuth"
              label={t('register.solar.form.panel.azimuth')}
              name={`sqmPanels.${index}.azimuth`}
              error={errors.sqmPanels?.[index]?.azimuth?.message}
              pattern={integerPattern}
              validate={validateAzimuth}
              value={sqmPanels?.[index]?.azimuth}
            />
            <SolarOrientationInput
              kind="tilt"
              label={t('register.solar.form.panel.tilt')}
              name={`sqmPanels.${index}.tilt`}
              error={errors.sqmPanels?.[index]?.tilt?.message}
              pattern={integerPattern}
              validate={validateTilt}
              value={sqmPanels?.[index]?.tilt}
            />
            {hasSolcellepanel(index) && (
              <SolarPanelAmountInput
                label={t('register.solar.form.panel.areaSolarCells')}
                suffix={t('register.solar.form.suffix.sqm')}
                name={`sqmPanels.${index}.area`}
                error={errors.sqmPanels?.[index]?.area?.message}
                pattern={decimalPattern}
                sliderMin={1}
                sliderMax={200}
                validate={validateArea}
                value={sqmPanels?.[index]?.area}
              />
            )}
          </PanelRowWrapper>

          {hasSolcelletakstein(index) && <SolcelletaksteinInput index={index} />}
          {
            // Do not show remove button on first item
            index > 0 && (
              <RemoveButtonWrapper>
                <RemoveButton onClick={() => remove(index)} />
              </RemoveButtonWrapper>
            )
          }
        </PanelCard>
      ))}
      <div>
        <Button variant="secondary" onClick={() => append({})}>
          {t('register.solar.form.buttons.add')}
        </Button>
      </div>
      {showNorthFacingAzimuthWarning && (
        <Alert data-color="warning">
          <Paragraph>{t('register.solar.form.panel.azimuth.northFacingWarning')}</Paragraph>
        </Alert>
      )}
    </PanelWrapper>
  );
};
